import React from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import BarGraph from 'components/Charts/BarGraph';
import Export from 'components/Export';
import Select from 'components/Select';
import Spinner from 'components/Spinner';

const CompareBarGraphContainer = styled.div`
  position: relative;
  display: grid;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};

  @media (max-width: 845px) {
    width: calc(100vw - 32px);
  }

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .selectSetsContainer {
    width: 80%;
    margin: 4% auto 0 auto;
  }

  .setListContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    margin-top: 5px;

    > div {
      margin: 0 !important;
      height: 30px !important;
      white-space: nowrap !important;
      width: initial !important;
    }
    .tag {
      width: initial !important;
    }
    > div:not(:last-child) {
      margin-right: 10px !important;
    }
  }

  .recharts-label-list {
    display: none;
  }
`;

const TooltipContainer = styled.div`
  padding: 10px;
  min-width: 150px;
`;

const LineInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  :not(:last-child) {
    margin-bottom: 6px;
  }

  .tooltipLineLabel {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    color: #4a4a4a;
  }

  .tooltipLineValue {
    margin-left: auto;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
    font-size: 14px;
    color: ${(props) => props.color};
  }
`;

class CompareBarGraph extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hoverExport: false,
      tagList: [],
      data: []
    };
  }

  componentDidMount() {
    this.setState({ data: this.props.data });
  }

  renderTooltip(data) {
    const colors = ['#e25d34', '#2B3D54', '#a5aaaf'];
    return (
      <TooltipContainer>
        {data
          .filter((item) => item && item.raw)
          .map(
            (set, index) =>
              set && (
                <LineInfo key={index} color={colors[index]}>
                  <p className="tooltipLineLabel">
                    {set.name} - {set.wine ? set.wine : ''}
                  </p>
                  <p className="tooltipLineValue">{set.raw}</p>
                </LineInfo>
              )
          )}
      </TooltipContainer>
    );
  }

  formatData() {
    const data = this.props.data || [];
    const rivals = this.props.rivals || [];
    let { tagList } = this.state;
    tagList = tagList.map((item) => item.value);
    let formattedData = [...data];
    rivals.forEach((rival) => {
      if (tagList.includes(rival.wine_name)) {
        formattedData.push(rival);
      }
    });
    const { isPrimeur } = this.props;
    formattedData = formattedData.map((item) => {
      const newItem = [];
      Object.keys(item).forEach((key) => {
        if (isPrimeur) {
          if (
            key.includes('_primeur') &&
            key != 'wxr_note' &&
            key != 'wxr_primeur'
          ) {
            if (item[key] && !isNaN(item[key]))
              newItem.push({
                name: key.replace('_primeur', '').toUpperCase(),
                value: item[key] > 100 ? 100 : item[key] < 75 ? 75 : item[key],
                wine: item.wine_name,
                raw: item[key.replace('_primeur', '_rawPrimeur')]
              });
          }
        } else {
          if (
            key.includes('_note') &&
            key != 'wxr_note' &&
            key != 'wxr_primeur'
          ) {
            if (item[key] && !isNaN(item[key])) {
              newItem.push({
                name: key.replace('_note', '').toUpperCase(),
                value: item[key],
                wine: item.wine_name,
                raw: item[key.replace('_note', '_rawNote')]
              });
            }
          }
        }
      });
      return newItem;
    });

    let setNames = [];
    let wines = [];
    formattedData.forEach((set) => {
      if (set && set.length > 0) {
        set.sort(function (a, b) {
          return String(a.name).localeCompare(String(b.name));
        });
        setNames.push(...set.map((item) => item.name));
        wines.push(set[0].wine);
      }
    });
    setNames = [...new Set(setNames)].sort();
    formattedData.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            value: 75,
            wine: wines[setIndex],
            raw: 0
          });
        }
      });
      if (formattedData.length === 1) {
        set.sort(function (a, b) {
          return b.value - a.value;
        });
      }
    });
    return formattedData;
  }

  getReferenceLineValue(data, formattedData) {
    const { isPrimeur } = this.props;
    if (data && data.length > 0 && formattedData) {
      if (isPrimeur) {
        return { value: data[0].wxr_primeur, label: 'WxR' };
      } else {
        return { value: data[0].wxr_note, label: 'WxR' };
      }
    }
    return undefined;
  }

  render() {
    let { data, rivals = [], exportName } = this.props;
    const formattedData = this.formatData(data);
    const { tagList } = this.state;
    const tooltipOffsetY = (tagList.length * 10 - 40) * -1;
    let labels = [];
    if (formattedData && formattedData.length > 0) {
      formattedData.forEach((set) => {
        if (set && set.length > 0) {
          labels.push(set[0].wine);
        }
      });
    }
    const referenceLine = this.getReferenceLineValue(data, formattedData);
    return (
      <CompareBarGraphContainer id="compare-graph">
        <Card>
          <CardHeader
            title={I18n.t('notes.compare.barGraphTitle')}
            fontSize="20px"
            iconColor="#e25d34"
            iconRight
            rightComponents={
              <Export
                jpg
                pdf
                showLabels
                targetElement="#compare-graph"
                name={exportName + I18n.t('notes.compare.barGraphTitle')}
                pdfOrientation="landscape"
              />
            }
          />
          <div className="compareBarGraphContainer">
            <BarGraph
              data={formattedData}
              isAnimationActive={false}
              height={400}
              yAxisProps={{
                width: 60,
                type: 'number',
                domain: [80, 100],
                ticks: [80, 85, 90, 95, 100],
                interval: 'preserveStartEnd'
              }}
              labels={labels}
              renderTooltip={(hoverData) => this.renderTooltip(hoverData)}
              tooltipOffset={{ x: 100, y: tooltipOffsetY }}
              colors={[
                '#e25d34',
                '#2B3D54',
                '#a5aaaf',
                '#B1B7BD',
                '#C9D0D6',
                '#DAE2E8'
              ]}
              referenceLine={referenceLine}
              margin={{ top: 0, right: 60, left: 0, bottom: 0 }}
              showLabel
            />
          </div>

          <div className="selectSetsContainer ignored-export">
            <Select
              placeholder={I18n.t('notes.compare.rivalPlaceholder')}
              minWidth="170px"
              isMulti
              value={this.state.tagList}
              dropdownProps={{
                styles: `margin-left:20px;margin-right:20px;`
              }}
              onChange={(value) =>
                this.setState({
                  tagList:
                    value && value.length > 5
                      ? value.slice(1, value.length)
                      : value
                })
              }
              options={rivals.map((rival) => ({
                value: rival.wine_name,
                label: rival.wine_name
              }))}
            />
          </div>
        </Card>
        <Spinner loading={this.props.loading} little />
      </CompareBarGraphContainer>
    );
  }
}

export default CompareBarGraph;
